import { getApiUrl } from "../api";
import { useEffect, useMemo, useState } from "react";
import Preloader from "../component/Preloader";
import Header from "../component/Header";
import LanguagePopup from "../component/LanguagePopup";
import Footer from "../component/Footer";
import FloatingWidgets from "../component/FloatingWidgets";
import HomeModals from "../component/HomeModals";
import useHomeInteractions from "../component/useHomeInteractions";
import { Link } from "../component/AppLink";
import { getCloudflareImageUrl } from "../utils/cloudflareImages";
import { ArabicFooter, ArabicHeader } from "./ArabicHome";

const getJwtToken = () => {
  const keys = ["token", "accessToken", "jwt", "authToken"];
  for (const key of keys) {
    const token = localStorage.getItem(key) || sessionStorage.getItem(key);
    if (token?.trim()) return token.replace(/^Bearer\s+/i, "").trim();
  }

  return (process.env.REACT_APP_JWT_TOKEN || "").replace(/^Bearer\s+/i, "").trim();
};

const stripHtml = (value) => String(value || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();

const splitLines = (value) => String(value || "")
  .replace(/<br\s*\/?>/gi, "\n")
  .replace(/<\/p>/gi, "\n")
  .replace(/<[^>]*>/g, " ")
  .split(/\r?\n|[•|]/)
  .map((item) => item.replace(/\s+/g, " ").trim())
  .filter(Boolean);

const getFetchOptions = (signal) => {
  const token = getJwtToken();
  const headers = { Accept: "application/json" };
  if (token) headers.Authorization = `Bearer ${token}`;

  return {
    headers,
    credentials: "include",
    signal,
  };
};

const mapMainContent = (item, language = "en") => {
  if (!item) return { middleBoxes: [] };
  const localized = (field) => stripHtml(language === "ar" ? (item[`${field}_ar`] || item[`${field}_en`] || item[field]) : (item[`${field}_en`] || item[field]));
  return {
    title: localized("title"), subtitle: localized("subtitle"), description: localized("description"),
    phone: stripHtml(item.phone), email: stripHtml(item.email), image: getCloudflareImageUrl(item.ImagePath, "w=1200,sharpen=1"),
    middleBoxes: [1, 2, 3, 4].map((number) => ({
      title: localized(`middlebox${number}title`), subtitle: localized(`middlebox${number}subtitle`),
      icon: getCloudflareImageUrl(item[`middlebox${number}icon`], "w=120,sharpen=1"),
    })).filter((box) => box.title || box.subtitle || box.icon),
    docTitle: localized("docreqtitle"), docSubtitle: localized("docreqsubtitle"), docDescription: localized("docreqdescription"),
    leftBoxTitle: localized("leftboxtitle"), leftBoxShortDescription: localized("leftboxshortdescription"),
    leftBoxDescription: language === "ar" ? (item.leftboxdescription_ar || item.leftboxdescription_en || "") : (item.leftboxdescription_en || ""),
    noticeTitle: localized("impnoticetitle"), noticeDescription: localized("impnoticedescription"),
  };
};

const mapCategory = (item, language = "en") => ({
  id: item.id || "",
  title: stripHtml(language === "ar" ? (item.title_ar || item.title_en || item.title) : item.title_en),
  badge: stripHtml(language === "ar" ? (item.badgetitle_ar || item.badgetitle_en || item.title_ar || item.title_en) : (item.badgetitle_en || item.title_en)),
  position: Number(item.position) || 0,
  documents: [],
});

const mapDocument = (item, language = "en") => ({
  id: item.id || "",
  catid: item.catid || "",
  title: stripHtml(language === "ar" ? (item.title_ar || item.title_en || item.title) : item.title_en) || "",
  position: Number(item.position) || 0,
});

export default function SubmitAidRequest({ language = "en" }) {
  const isArabic = language === "ar";
   const [agreed, setAgreed] = useState(false);
  const [error, setError] = useState("");

  const handleStart = (e) => {
    if (!agreed) {
      e.preventDefault(); // stop navigation
      setError(isArabic ? "يرجى الموافقة على الشروط والأحكام قبل بدء الطلب." : "Please agree to the terms and conditions before starting the application.");
    } else {
      setError("");
    }
  };
  const interactions = useHomeInteractions();
  const [content, setContent] = useState({ middleBoxes: [] });
  const [categories, setCategories] = useState([]);
  const [statusMessage, setStatusMessage] = useState("");
  const text = isArabic ? {
    portal: "بوابة طلب المساعدة الإلكترونية", secureProcess: "إجراءات آمنة", upload: "رفع المستندات", access: "وصول إلكتروني", start: "ابدأ الطلب", viewDocuments: "عرض المستندات المطلوبة", secureSubmission: "إرسال آمن", protected: "معلوماتك محمية", support: "الدعم", email: "راسلنا", notice: "تنبيه المحتوى", agree: "أوافق على جميع الشروط المحددة من مؤسسة تراحم الخيرية، وأؤكد أن جميع البيانات المقدمة صحيحة."
  } : {
    portal: "Online Aid Request Portal", secureProcess: "Secure Process", upload: "Document Upload", access: "Online Access", start: "Start Application", viewDocuments: "View Required Documents", secureSubmission: "Secure Submission", protected: "Your information is protected", support: "Support", email: "Email Us", notice: "Content notice", agree: "I agree to all conditions specified by the Tarahum Charity Foundation and confirm that all submitted data is accurate."
  };

  const sideList = useMemo(() => {
    const items = splitLines(content.leftBoxDescription);
    return items;
  }, [content.leftBoxDescription]);

  useEffect(() => {
    document.title = isArabic ? "تقديم طلب مساعدة | مؤسسة تراحم الخيرية" : "Submit Aid Request | Tarahum Charity Foundation";
  }, [isArabic]);

  useEffect(() => {
    if (!isArabic) return undefined;
    document.documentElement.lang = "ar";
    document.documentElement.dir = "rtl";
    document.body.classList.add("arabic-layout-page");
    return () => {
      document.body.classList.remove("arabic-layout-page");
      document.documentElement.lang = "en";
      document.documentElement.dir = "ltr";
    };
  }, [isArabic]);

  useEffect(() => {
    const controller = new AbortController();

    const loadPage = async () => {
      try {
        const [mainResponse, categoryResponse, requiredDocResponse] = await Promise.all([
          fetch(getApiUrl("/api/GetAidRequestMainList.ashx?pageSize=1"), getFetchOptions(controller.signal)),
          fetch(getApiUrl("/api/GetAidRequestDocCategoryList.ashx?pageSize=100"), getFetchOptions(controller.signal)),
          fetch(getApiUrl("/api/GetRequiredDocAllTypeList.ashx?pageSize=500"), getFetchOptions(controller.signal)),
        ]);

        if (!mainResponse.ok || !categoryResponse.ok || !requiredDocResponse.ok) {
          throw new Error("Unable to load aid request content.");
        }

        const [mainResult, categoryResult, requiredDocResult] = await Promise.all([
          mainResponse.json(),
          categoryResponse.json(),
          requiredDocResponse.json(),
        ]);

        const firstMainRow = Array.isArray(mainResult?.data) ? mainResult.data[0] : null;
        const apiCategories = (Array.isArray(categoryResult?.data) ? categoryResult.data : []).map((item) => mapCategory(item, language)).filter((item) => item.id);
        const apiDocuments = (Array.isArray(requiredDocResult?.data) ? requiredDocResult.data : []).map((item) => mapDocument(item, language)).filter((item) => item.title);

        if (firstMainRow) setContent(mapMainContent(firstMainRow, language));

        if (apiCategories.length) {
          const grouped = apiCategories.map((category) => ({
            ...category,
            documents: apiDocuments
              .filter((document) => String(document.catid) === String(category.id))
              .sort((left, right) => left.position - right.position)
              .map((document) => document.title),
          }));

          setCategories(grouped.filter((category) => category.documents.length));
        }

        setStatusMessage("");
      } catch (error) {
        if (error.name !== "AbortError") {
          setStatusMessage(isArabic ? "تعذر تحميل إرشادات المستندات حالياً." : "Unable to load document guidance right now.");
          console.warn("Unable to load submit aid request content", error);
        }
      }
    };

    loadPage();
    return () => controller.abort();
  }, [isArabic, language]);

  return (
    <>
      <Preloader />
      {isArabic ? <ArabicHeader /> : <><Header /><LanguagePopup /></>}

      <section style={{ padding: "15px" }}>
        <div className="sad-hero-new" style={{ borderRadius: "15px", overflow: "hidden" }}>
          <div className="container">
            <div className="sad-container">
              <div className="sad-hero-new-grid">
                <div className="sad-hero-new-content">
                  <div className="sad-hero-badge">
                    <span></span>
                    {text.portal}
                  </div>

                  <h1>
                    {content.title} <span>{content.subtitle}</span>
                  </h1>

                  <p>{content.description}</p>

                  <div className="sad-hero-stats">
                    <div>
                      <strong>100%</strong>
                      <span>{text.secureProcess}</span>
                    </div>
                    <div>
                      <strong>PDF</strong>
                      <span>{text.upload}</span>
                    </div>
                    <div>
                      <strong>24/7</strong>
                      <span>{text.access}</span>
                    </div>
                  </div>

                  <div className="sad-hero-actions">
                    <a href="#start-application" className="sad-btn sad-btn-primary">{text.start}</a>
                    <a href="#required-documents" className="sad-btn sad-btn-outline">{text.viewDocuments}</a>
                  </div>
                </div>

                <div className="sad-hero-new-visual">
                  <div className="sad-hero-img-card">
                    <img src={content.image} alt="Aid request support" />
                    <div className="sad-floating-card sad-card-bottom">
                      <div className="sad-floating-icon">
                        <i className="fa-solid fa-lock"></i>
                      </div>
                      <div>
                        <strong>{text.secureSubmission}</strong>
                        <span>{text.protected}</span>
                      </div>
                    </div>
                  </div>

                  <div className="sad-contact-modern">
                    <div className="sad-contact-modern-box">
                      <span>{text.support}</span>
                      <strong>{content.phone}</strong>
                    </div>
                    <div className="sad-contact-modern-box">
                      <span>{text.email}</span>
                      <strong>{content.email}</strong>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="sad-section">
        <div className="container">
          <div className="sad-container">
            <div className="sad-info-grid">
              {content.middleBoxes.map((box) => (
                <div className="sad-info-card" key={`${box.title}-${box.subtitle}`}>
                  <div className="sad-info-icon">
                    <img src={box.icon} alt="" />
                  </div>
                  <h3>{box.title}</h3>
                  <p>{box.subtitle}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="sad-section" id="required-documents">
        <div className="container">
          <div className="sad-container">
            <div className="sad-section-head">
              <small>{content.docSubtitle}</small>
              <h2>{content.docTitle}</h2>
              <p>{content.docDescription}</p>
            </div>

            {statusMessage ? (
              <div className="in-kind-alert" role="alert">
                <span className="in-kind-alert-icon">
                  <i className="fa-solid fa-circle-info"></i>
                </span>
                <span>
                  <strong>{text.notice}</strong>
                  <small>{statusMessage}</small>
                </span>
              </div>
            ) : null}

            <div className="sad-doc-layout">
              <aside className="sad-side-card">
                <h3>{content.leftBoxTitle}</h3>
                <p>{content.leftBoxShortDescription}</p>
                <ul className="sad-side-list">
                  {sideList.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </aside>

              <div className="sad-docs">
                {categories.map((category) => (
                  <div className="sad-doc-group" key={category.id}>
                    <div className="sad-doc-title">
                      <h3>{category.title}</h3>
                      <span>{category.badge}</span>
                    </div>
                    <ul className="sad-doc-list">
                      {category.documents.map((document) => (
                        <li key={document}>{document}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
 

            <div className="sad-notice" id="start-application">
              <div className="sad-notice-icon">
                <i className="fa-solid fa-triangle-exclamation"></i>
              </div>
              <div>
                <h3>{content.noticeTitle}</h3>
                <p>{content.noticeDescription}</p>
              </div>
            </div>
{error && (
  <div className="quk-dnt">
       <div className="quick-donation-alert" role="alert">
                    <i className="fa-solid fa-circle-exclamation"></i>
          {error}
        </div>
        </div>
      )}
            <div className="sad-apply-box">
              <label className="sad-check">
                <input
            type="checkbox"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
          />
                <span>{text.agree}</span>
              </label>
              <Link to="/services" className="sad-btn sad-btn-primary" id="sadStartBtn"  onClick={handleStart}>{text.start}</Link>
            </div>
          </div>
        </div>
      </section>

      {isArabic ? <ArabicFooter /> : <Footer />}
      <FloatingWidgets {...interactions} />
      <HomeModals {...interactions} />
    </>
  );
}
